import { useEffect, useRef, useState } from "react";

export const RMS_THRESHOLD_DEFAULT = 0.018;
export const SILENCE_TIMEOUT_MS_DEFAULT = 1200;
export const MIN_VOICE_MS_DEFAULT = 180;
export const TARGET_SAMPLE_RATE_DEFAULT = 24000;

export type AudioRecorderStatus = "idle" | "requesting" | "recording" | "error";

type LegacyWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

type UseAudioRecorderOptions = {
  onChunk?: (audioBase64: string) => void;
  onSilenceTimeout?: () => void;
  silenceTimeoutMs?: number;
  rmsThreshold?: number;
  minVoiceMs?: number;
  targetSampleRate?: number;
  stopOnSilenceTimeout?: boolean;
};

type StartRecorderOptions = {
  sampleRate?: number;
};

function createAudioContext(options?: AudioContextOptions): AudioContext {
  const AudioContextCtor = window.AudioContext ?? (window as LegacyWindow).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error("目前瀏覽器不支援 Web Audio API。");
  }
  return new AudioContextCtor(options);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, Math.min(index + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

export function calculateRms(input: Float32Array): number {
  if (!input.length) {
    return 0;
  }
  let total = 0;
  for (let index = 0; index < input.length; index += 1) {
    const sample = input[index] ?? 0;
    total += sample * sample;
  }
  return Math.sqrt(total / input.length);
}

export function floatToPcm16(
  input: Float32Array,
  inputRate: number,
  outputRate: number,
): Uint8Array {
  if (!input.length) {
    return new Uint8Array(0);
  }
  const ratio = inputRate / outputRate;
  const outputLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Uint8Array(outputLength * 2);
  const view = new DataView(output.buffer);

  for (let index = 0; index < outputLength; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(input.length, Math.floor((index + 1) * ratio));
    let total = 0;
    let count = 0;
    for (let sampleIndex = start; sampleIndex < Math.max(start + 1, end); sampleIndex += 1) {
      total += input[sampleIndex] ?? 0;
      count += 1;
    }
    const sample = Math.max(-1, Math.min(1, total / Math.max(1, count)));
    const pcm = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    view.setInt16(index * 2, Math.round(pcm), true);
  }

  return output;
}

export function useAudioRecorder({
  onChunk,
  onSilenceTimeout,
  silenceTimeoutMs = SILENCE_TIMEOUT_MS_DEFAULT,
  rmsThreshold = RMS_THRESHOLD_DEFAULT,
  minVoiceMs = MIN_VOICE_MS_DEFAULT,
  targetSampleRate = TARGET_SAMPLE_RATE_DEFAULT,
  stopOnSilenceTimeout = true,
}: UseAudioRecorderOptions = {}) {
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState<AudioRecorderStatus>("idle");
  const [silenceRemaining, setSilenceRemaining] = useState(
    Math.floor(silenceTimeoutMs / 1000),
  );

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const muteGainRef = useRef<GainNode | null>(null);
  const recordingRef = useRef(false);
  const silenceTimerRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const voiceAccumMsRef = useRef(0);
  const timerPausedRef = useRef(false);
  const speechActiveRef = useRef(false);
  const preSpeechChunksRef = useRef<string[]>([]);
  const onChunkRef = useRef(onChunk);
  const onSilenceTimeoutRef = useRef(onSilenceTimeout);

  useEffect(() => {
    onChunkRef.current = onChunk;
  }, [onChunk]);

  useEffect(() => {
    onSilenceTimeoutRef.current = onSilenceTimeout;
  }, [onSilenceTimeout]);

  function clearSilenceTimer() {
    if (silenceTimerRef.current) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }

  function cleanupResources() {
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    muteGainRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close();
    }

    processorRef.current = null;
    sourceRef.current = null;
    muteGainRef.current = null;
    streamRef.current = null;
    audioContextRef.current = null;
    speechActiveRef.current = false;
    voiceAccumMsRef.current = 0;
    preSpeechChunksRef.current = [];
    timerPausedRef.current = false;
  }

  function stop() {
    clearSilenceTimer();
    cleanupResources();
    recordingRef.current = false;
    setRecording(false);
    setStatus("idle");
    setSilenceRemaining(Math.floor(silenceTimeoutMs / 1000));
  }

  function startSilenceTimer() {
    clearSilenceTimer();
    const deadline = Date.now() + silenceTimeoutMs;
    setSilenceRemaining(Math.ceil(silenceTimeoutMs / 1000));
    countdownIntervalRef.current = window.setInterval(() => {
      const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSilenceRemaining(secondsLeft);
    }, 250);
    silenceTimerRef.current = window.setTimeout(() => {
      clearSilenceTimer();
      speechActiveRef.current = false;
      voiceAccumMsRef.current = 0;
      preSpeechChunksRef.current = [];
      if (stopOnSilenceTimeout) {
        stop();
      }
      onSilenceTimeoutRef.current?.();
    }, silenceTimeoutMs);
  }

  function pauseSilenceTimer(reset = true) {
    timerPausedRef.current = true;
    clearSilenceTimer();
    if (reset) {
      voiceAccumMsRef.current = 0;
      preSpeechChunksRef.current = [];
      setSilenceRemaining(Math.floor(silenceTimeoutMs / 1000));
    }
  }

  function resumeSilenceTimer(reset = true) {
    timerPausedRef.current = false;
    if (!recordingRef.current) {
      return;
    }
    if (!speechActiveRef.current) {
      setSilenceRemaining(Math.floor(silenceTimeoutMs / 1000));
      return;
    }
    if (reset) {
      startSilenceTimer();
    } else if (!silenceTimerRef.current) {
      startSilenceTimer();
    }
  }

  async function start(options: StartRecorderOptions = {}) {
    if (recordingRef.current) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      throw new Error("目前瀏覽器不支援麥克風存取。");
    }

    const outputSampleRate = options.sampleRate ?? targetSampleRate;

    try {
      setStatus("requesting");
      setSilenceRemaining(Math.floor(silenceTimeoutMs / 1000));

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: outputSampleRate,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      const audioContext = createAudioContext();
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const muteGain = audioContext.createGain();
      muteGain.gain.value = 0;

      processor.onaudioprocess = (audioEvent) => {
        const channel = audioEvent.inputBuffer.getChannelData(0);
        const chunkMs = (channel.length / audioContext.sampleRate) * 1000;
        const rms = calculateRms(channel);
        const base64Audio = bytesToBase64(
          floatToPcm16(channel, audioContext.sampleRate, outputSampleRate),
        );
        if (!base64Audio) {
          return;
        }

        const voiced = rms >= rmsThreshold;
        if (voiced) {
          voiceAccumMsRef.current += chunkMs;
          if (!speechActiveRef.current) {
            preSpeechChunksRef.current.push(base64Audio);
            if (preSpeechChunksRef.current.length > 12) {
              preSpeechChunksRef.current.shift();
            }
            if (voiceAccumMsRef.current >= minVoiceMs) {
              speechActiveRef.current = true;
              for (const buffered of preSpeechChunksRef.current) {
                onChunkRef.current?.(buffered);
              }
              preSpeechChunksRef.current = [];
            }
          } else {
            onChunkRef.current?.(base64Audio);
          }

          if (speechActiveRef.current && !timerPausedRef.current) {
            startSilenceTimer();
          }
          return;
        }

        if (!speechActiveRef.current) {
          voiceAccumMsRef.current = 0;
          preSpeechChunksRef.current = [];
          return;
        }

        onChunkRef.current?.(base64Audio);
        if (!timerPausedRef.current && !silenceTimerRef.current) {
          startSilenceTimer();
        }
      };

      source.connect(processor);
      processor.connect(muteGain);
      muteGain.connect(audioContext.destination);

      streamRef.current = stream;
      audioContextRef.current = audioContext;
      sourceRef.current = source;
      processorRef.current = processor;
      muteGainRef.current = muteGain;
      recordingRef.current = true;
      setRecording(true);
      setStatus("recording");
    } catch (caughtError) {
      clearSilenceTimer();
      cleanupResources();
      recordingRef.current = false;
      setRecording(false);
      setStatus("error");
      throw caughtError;
    }
  }

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      cleanupResources();
      recordingRef.current = false;
    };
  }, [silenceTimeoutMs]);

  return {
    recording,
    status,
    silenceRemaining,
    start,
    stop,
    pauseSilenceTimer,
    resumeSilenceTimer,
  };
}
